import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  InputBase,
  IconButton,
  Divider,
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  Chip,
  Typography,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn,
  Tune as FilterIcon,
  KeyboardArrowDown,
  Clear,
} from '@mui/icons-material';

type SearchBarProps = {
  variant?: 'standard' | 'hero' | 'compact';
  showFilters?: boolean;
  onSearch?: (searchParams: any) => void;
};

const SearchBar = ({ 
  variant = 'standard', 
  showFilters = true,
  onSearch 
}: SearchBarProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, any>>({
    propertyType: '',
    priceRange: '',
    bedrooms: '',
    bathrooms: '',
  });
  
  const [anchorEl, setAnchorEl] = useState<Record<string, HTMLElement | null>>({
    propertyType: null,
    priceRange: null,
    bedrooms: null,
    bathrooms: null,
  });

  const propertyTypes = [
    'All Properties', 'House', 'Apartment', 'Condo', 'Townhouse', 'Land'
  ];
  
  const priceRanges = [
    'Any Price', '$0-$200K', '$200K-$400K', '$400K-$600K', '$600K-$800K', '$800K-$1M', '$1M+'
  ];
  
  const bedroomsOptions = ['Any', '1+', '2+', '3+', '4+', '5+'];
  const bathroomsOptions = ['Any', '1+', '1.5+', '2+', '3+', '4+'];

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, filterType: string) => {
    setAnchorEl({
      ...anchorEl,
      [filterType]: event.currentTarget,
    });
  };

  const handleCloseMenu = (filterType: string) => {
    setAnchorEl({
      ...anchorEl,
      [filterType]: null,
    });
  };

  const handleFilterSelect = (filterType: string, value: string) => {
    setSelectedFilters({
      ...selectedFilters,
      [filterType]: value,
    });
    handleCloseMenu(filterType);
  };

  const handleClearFilter = (filterType: string) => {
    setSelectedFilters({
      ...selectedFilters,
      [filterType]: '',
    });
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const searchParams = {
      query: searchQuery,
      ...selectedFilters,
    };
    
    if (onSearch) {
      onSearch(searchParams);
    } else {
      // Build query string from searchParams
      const queryParams = new URLSearchParams();
      if (searchQuery) queryParams.append('query', searchQuery);
      Object.entries(selectedFilters).forEach(([key, value]) => {
        if (value && value !== 'All Properties' && value !== 'Any' && value !== 'Any Price') {
          queryParams.append(key, value);
        }
      });
      
      navigate(`/listings?${queryParams.toString()}`);
    }
  };

  const isHeroVariant = variant === 'hero';
  const isCompactVariant = variant === 'compact';

  return (
    <Box
      component="form"
      onSubmit={handleSearchSubmit}
      sx={{
        width: '100%',
        maxWidth: isHeroVariant ? '900px' : '100%',
        mx: 'auto',
      }}
    >
      <Paper
        elevation={isHeroVariant ? 3 : 1}
        sx={{
          p: isHeroVariant ? 2 : 1,
          display: 'flex',
          flexDirection: isCompactVariant ? 'row' : 'column',
          borderRadius: isHeroVariant ? 2 : 1,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* Main search input */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            px: 1,
            mb: isCompactVariant ? 0 : 1,
          }}
        >
          <IconButton sx={{ p: '10px' }} aria-label="location">
            <LocationOn />
          </IconButton>
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder={isHeroVariant 
              ? "Enter an address, neighborhood, city, or ZIP code..." 
              : "Search properties..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            inputProps={{ 'aria-label': 'search properties' }}
          />
          {searchQuery && (
            <IconButton 
              size="small" 
              aria-label="clear search" 
              onClick={() => setSearchQuery('')}
            >
              <Clear fontSize="small" />
            </IconButton>
          )}
          {isCompactVariant && (
            <IconButton type="submit" sx={{ p: '10px' }} aria-label="search">
              <SearchIcon />
            </IconButton>
          )}
        </Box>

        {/* Filter options */}
        {showFilters && !isCompactVariant && (
          <Stack 
            direction={isHeroVariant ? "row" : "column"} 
            spacing={isHeroVariant ? 2 : 1}
            divider={isHeroVariant ? <Divider orientation="vertical" flexItem /> : undefined}
            sx={{ 
              width: '100%',
              mb: isHeroVariant ? 2 : 1,
            }}
          >
            {/* Property Type Filter */}
            <Box>
              <Button
                color="inherit"
                onClick={(e) => handleOpenMenu(e, 'propertyType')}
                endIcon={<KeyboardArrowDown />}
                sx={{ textTransform: 'none', justifyContent: 'space-between', width: isHeroVariant ? 'auto' : '100%' }}
              >
                <Typography variant="body2" noWrap>
                  {selectedFilters.propertyType || 'Property Type'}
                </Typography>
              </Button>
              <Menu
                anchorEl={anchorEl.propertyType}
                open={Boolean(anchorEl.propertyType)}
                onClose={() => handleCloseMenu('propertyType')}
              >
                {propertyTypes.map((type) => (
                  <MenuItem
                    key={type}
                    selected={selectedFilters.propertyType === type}
                    onClick={() => handleFilterSelect('propertyType', type)}
                  >
                    {type}
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {/* Price Range Filter */}
            <Box>
              <Button
                color="inherit"
                onClick={(e) => handleOpenMenu(e, 'priceRange')}
                endIcon={<KeyboardArrowDown />}
                sx={{ textTransform: 'none', justifyContent: 'space-between', width: isHeroVariant ? 'auto' : '100%' }}
              >
                <Typography variant="body2" noWrap>
                  {selectedFilters.priceRange || 'Price Range'}
                </Typography>
              </Button>
              <Menu
                anchorEl={anchorEl.priceRange}
                open={Boolean(anchorEl.priceRange)}
                onClose={() => handleCloseMenu('priceRange')}
              >
                {priceRanges.map((range) => (
                  <MenuItem
                    key={range}
                    selected={selectedFilters.priceRange === range}
                    onClick={() => handleFilterSelect('priceRange', range)}
                  >
                    {range}
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {/* Bedrooms Filter */}
            <Box>
              <Button
                color="inherit"
                onClick={(e) => handleOpenMenu(e, 'bedrooms')}
                endIcon={<KeyboardArrowDown />}
                sx={{ textTransform: 'none', justifyContent: 'space-between', width: isHeroVariant ? 'auto' : '100%' }}
              >
                <Typography variant="body2" noWrap>
                  {selectedFilters.bedrooms || 'Bedrooms'}
                </Typography>
              </Button>
              <Menu
                anchorEl={anchorEl.bedrooms}
                open={Boolean(anchorEl.bedrooms)}
                onClose={() => handleCloseMenu('bedrooms')}
              >
                {bedroomsOptions.map((option) => (
                  <MenuItem
                    key={option}
                    selected={selectedFilters.bedrooms === option}
                    onClick={() => handleFilterSelect('bedrooms', option)}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {/* Bathrooms Filter */}
            <Box>
              <Button
                color="inherit"
                onClick={(e) => handleOpenMenu(e, 'bathrooms')}
                endIcon={<KeyboardArrowDown />}
                sx={{ textTransform: 'none', justifyContent: 'space-between', width: isHeroVariant ? 'auto' : '100%' }}
              >
                <Typography variant="body2" noWrap>
                  {selectedFilters.bathrooms || 'Bathrooms'}
                </Typography>
              </Button>
              <Menu
                anchorEl={anchorEl.bathrooms}
                open={Boolean(anchorEl.bathrooms)}
                onClose={() => handleCloseMenu('bathrooms')}
              >
                {bathroomsOptions.map((option) => (
                  <MenuItem
                    key={option}
                    selected={selectedFilters.bathrooms === option}
                    onClick={() => handleFilterSelect('bathrooms', option)}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {!isHeroVariant && <Box sx={{ flexGrow: 1 }} />}

            {/* More Filters Button */}
            <Tooltip title="More filters">
              <IconButton aria-label="more filters" color="primary">
                <FilterIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        {/* Selected filters */}
        {showFilters && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: 'wrap',
              gap: 1,
              mb: isHeroVariant ? 2 : 1,
              display: Object.values(selectedFilters).some(Boolean) ? 'flex' : 'none',
            }}
          >
            {Object.entries(selectedFilters).map(([key, value]) => {
              if (!value || value === 'All Properties' || value === 'Any' || value === 'Any Price') return null;
              return (
                <Chip
                  key={key}
                  label={value}
                  onDelete={() => handleClearFilter(key)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              );
            })}
          </Stack>
        )}

        {/* Search button */}
        {!isCompactVariant && (
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            size={isHeroVariant ? "large" : "medium"}
            sx={{
              alignSelf: 'center',
              minWidth: isHeroVariant ? 150 : 'auto',
              fontWeight: 600,
            }}
          >
            Search
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default SearchBar;